import { onMounted, onUnmounted } from "vue";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useKeycloakStore } from "../pinia-stores/keycloak.js";
import { markSessionExpiry } from "../session-expiry.js";

const IDLE_TIMEOUT_MS = 10 * 60 * 1000;
const TOKEN_REFRESH_MIN_VALIDITY = 70;
const ACTIVITY_EVENTS = [
  "pointerdown",
  "mousemove",
  "keydown",
  "touchstart",
  "scroll",
];

export function useIdleSession() {
  const keycloakStore = useKeycloakStore();
  let idleTimer = null;
  let lastActivityAt = Date.now();
  let resumeListener = null;

  function clearIdleTimer() {
    if (idleTimer) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  }

  function scheduleIdleCheck() {
    clearIdleTimer();
    idleTimer = setTimeout(onIdle, IDLE_TIMEOUT_MS);
  }

  function handleActivity() {
    lastActivityAt = Date.now();
    scheduleIdleCheck();
  }

  function endSession() {
    markSessionExpiry();
    keycloakStore.forceLocalLogout();
  }

  async function expireSession() {
    if (!keycloakStore.authenticated) return;

    try {
      await keycloakStore.updateToken(TOKEN_REFRESH_MIN_VALIDITY);
    } catch {
      // token refresh already failed; local logout below cleans up
    }

    endSession();
  }

  function onIdle() {
    idleTimer = null;
    if (!keycloakStore.authenticated) return;
    console.info("Session idle timeout reached, ending session");
    void expireSession();
  }

  function handleResume() {
    if (Date.now() - lastActivityAt >= IDLE_TIMEOUT_MS) {
      void expireSession();
      return;
    }
    scheduleIdleCheck();

    if (!keycloakStore.authenticated) return;
    keycloakStore.updateToken(TOKEN_REFRESH_MIN_VALIDITY).catch(() => {
      endSession();
    });
  }

  function handleVisibilityChange() {
    if (document.visibilityState === "visible") {
      handleResume();
    } else {
      clearIdleTimer();
    }
  }

  onMounted(() => {
    if (typeof window === "undefined") return;
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", handleVisibilityChange);
    scheduleIdleCheck();

    if (Capacitor.isNativePlatform() && App?.addListener) {
      App.addListener("resume", () => {
        handleResume();
      })
        .then((listener) => {
          resumeListener = listener;
        })
        .catch((error) => {
          console.warn("Could not listen for app resume", error);
        });
    }
  });

  onUnmounted(() => {
    clearIdleTimer();
    ACTIVITY_EVENTS.forEach((event) => {
      window.removeEventListener(event, handleActivity);
    });
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    resumeListener?.remove?.();
  });

  return {
    expireSession,
  };
}
